/**
 * `EntityDecoratorExtension` — Tiptap extension that decorates inline
 * mentions of known entities in the note body as clickable chips.
 *
 * Sister to HashtagDecoratorExtension; differs in:
 *   - Data-driven (needs the per-note entity list from backend) rather than
 *     regex-only. Passes through a Tiptap command `setEntityList(entities)`
 *     so the parent React component can push the latest data after the
 *     companion query resolves.
 *   - Matches on `surfaceForms` from each entity's mention record — the
 *     literal strings extractor wrote (e.g. "Tim", "Tim Nelson"). Each
 *     match becomes a span decoration with `data-entity-id`.
 *
 * Click handling is the same delegated `[data-entity-id]` listener
 * RichTextEditorPane installs for the hashtag chips (catches both).
 *
 * Performance notes:
 *   - Decorations rebuild on every `docChanged` transaction (cheap-ish
 *     for typical notes — O(text × surface_forms)) AND on every
 *     setEntityList call (when companion data arrives).
 *   - Skips `codeBlock` and inline `code` mark spans so a code reference
 *     to a "User" class doesn't decorate it as a person entity.
 *   - Word-boundary matching `\bSF\b` to avoid sub-word false positives
 *     ("apple" inside "applesauce" would NOT match the Apple entity).
 *   - Case-INSENSITIVE matching — entity surface forms may be capitalised
 *     differently than how they appear in the prose.
 *   - Deduplicates overlapping matches at the same position so a longer
 *     surface form ("Tim Nelson") wins over a shorter one ("Tim") when
 *     they overlap. Both belong to the same entity, but only the longer
 *     range gets a decoration.
 */

import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PmNode } from '@tiptap/pm/model';
import { debouncedRecomputeView } from './decoratorRecompute';

export interface EntityRef {
  /** The EntityId — used as `data-entity-id` and for the search filter URL. */
  entityId: string;
  /** Literal strings the extractor saw in the doc. Decorator finds these
   *  case-insensitively at word boundaries. */
  surfaceForms: string[];
}

interface PluginValue {
  entities: EntityRef[];
  decorations: DecorationSet;
}

export const entityDecoratorPluginKey = new PluginKey<PluginValue>(
  'entityDecorator',
);

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    entityDecorator: {
      /** Update the entity list the decorator scans for. Triggers a fresh
       *  decoration pass over the current doc. */
      setEntityList: (entities: EntityRef[]) => ReturnType;
    };
  }
}

export const EntityDecoratorExtension = Extension.create({
  name: 'entityDecorator',

  addProseMirrorPlugins() {
    return [
      new Plugin<PluginValue>({
        key: entityDecoratorPluginKey,
        state: {
          init: () => ({ entities: [], decorations: DecorationSet.empty }),
          apply(tr, prev) {
            const meta = tr.getMeta(entityDecoratorPluginKey) as
              | { entities?: EntityRef[]; recompute?: boolean }
              | undefined;
            const entities = meta?.entities ?? prev.entities;
            const entitiesChanged =
              meta?.entities !== undefined && meta.entities !== prev.entities;
            // Full re-scan when the entity list changes (companion data arrived —
            // not a hot path) or on an explicit (debounced) recompute after edits.
            // Every keystroke otherwise just position-maps the existing set, with
            // the debounced view scheduling the re-scan shortly after typing stops
            // (perf, 2026-06-10).
            if (entitiesChanged || meta?.recompute) {
              return { entities, decorations: computeEntityDecorations(tr.doc, entities) };
            }
            if (tr.docChanged) {
              return { entities, decorations: prev.decorations.map(tr.mapping, tr.doc) };
            }
            return prev;
          },
        },
        view: debouncedRecomputeView(entityDecoratorPluginKey),
        props: {
          decorations(state) {
            return entityDecoratorPluginKey.getState(state)?.decorations;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setEntityList:
        (entities: EntityRef[]) =>
        ({ tr, dispatch }) => {
          if (dispatch !== undefined) {
            dispatch(tr.setMeta(entityDecoratorPluginKey, { entities }));
          }
          return true;
        },
    };
  },
});

/**
 * Walk every text node in the doc, finding `surfaceForms` at word
 * boundaries for each entity. Returns a fresh DecorationSet.
 *
 * Match strategy (2026-05-18 (v2) — first-occurrence only):
 *   1. Sort entities so that each entity's LONGEST surface form runs
 *      first within that entity (so "Tim Nelson" wins over "Tim").
 *   2. Walk the doc text-node-by-text-node, IN DOCUMENT ORDER.
 *   3. Track a Set<entityId> of entities that have already been
 *      decorated SOMEWHERE in the doc.
 *   4. Within a text node, collect candidate matches across all entities
 *      whose ids are NOT yet in the decorated set.
 *   5. Resolve overlaps: keep the LONGER match; drop any candidate whose
 *      range overlaps an already-kept one within the same text node.
 *   6. Emit a Decoration.inline per surviving match; add each entityId
 *      to the decorated set so subsequent occurrences (in this or
 *      later text nodes) are skipped.
 *
 * Rationale: decorating every occurrence of a frequently-mentioned entity
 * ("Tim" appearing 20 times in a meeting note) overwhelms the prose.
 * First-occurrence-only marks the entity's introduction without burying
 * the rest of the body in chips.
 */
function computeEntityDecorations(
  doc: PmNode,
  entities: EntityRef[],
): DecorationSet {
  if (entities.length === 0) return DecorationSet.empty;

  // Pre-build a flat list of (entityId, surfaceForm) pairs, longest first, and
  // COMPILE each surface-form regex ONCE here (perf, 2026-06-10) — previously a
  // `new RegExp` was constructed per surface-form *per text node*, which on a
  // large note (every table cell is a text node) is thousands of regex compiles
  // per scan. Now it's one compile per surface form, reused across all nodes.
  const pairs: Array<{ entityId: string; surface: string; re: RegExp }> = [];
  for (const ent of entities) {
    for (const sf of ent.surfaceForms) {
      if (sf.length === 0) continue;
      pairs.push({
        entityId: ent.entityId,
        surface: sf,
        re: new RegExp(`\\b${escapeRegex(sf)}\\b`, 'gi'),
      });
    }
  }
  pairs.sort((a, b) => b.surface.length - a.surface.length);

  const decorations: Decoration[] = [];
  // Doc-level set: an entity decorated ANYWHERE in the doc is skipped for
  // the rest of the walk. Persists across text-node boundaries.
  const decoratedEntityIds = new Set<string>();

  doc.descendants((node, pos, parent) => {
    // U7 (2026-07-05): never decorate inside headings — highlight boxes over
    // H1–H3 titles read as a ransom note. The first BODY occurrence still gets
    // the chip (skipped heading text never enters `decoratedEntityIds`).
    if (node.type.name === 'heading') return false;
    if (parent !== null && parent.type.name === 'codeBlock') return false;
    if (!node.isText) return true;
    if (node.marks.some((m) => m.type.name === 'code')) return true;
    const text = node.text ?? '';
    if (text.length === 0) return true;
    // Collect candidate matches WITHIN this text node, longest-first,
    // skipping entities already decorated upstream in the doc.
    type Candidate = { start: number; end: number; entityId: string };
    const candidates: Candidate[] = [];
    for (const { entityId, surface, re } of pairs) {
      if (decoratedEntityIds.has(entityId)) continue;
      re.lastIndex = 0; // reused across nodes — reset the /g cursor each scan
      let m: RegExpExecArray | null;
      while ((m = re.exec(text)) !== null) {
        candidates.push({
          start: m.index,
          end: m.index + surface.length,
          entityId,
        });
      }
    }
    // Sort candidates by document position so we keep the FIRST occurrence
    // of each entity within this text node (not the first encountered
    // surface form — which would be the longest-prefix tie when an entity
    // has multiple surface forms appearing at different positions).
    candidates.sort((a, b) => a.start - b.start);
    // Walk in doc order. For each candidate: skip if its entity was already
    // decorated (in a prior iteration this same loop) OR if it overlaps an
    // already-accepted range in this text node.
    const accepted: Candidate[] = [];
    for (const c of candidates) {
      if (decoratedEntityIds.has(c.entityId)) continue;
      const overlap = accepted.some(
        (a) => c.start < a.end && c.end > a.start,
      );
      if (overlap) continue;
      accepted.push(c);
      decoratedEntityIds.add(c.entityId);
    }
    for (const a of accepted) {
      decorations.push(
        Decoration.inline(pos + a.start, pos + a.end, {
          class: 'entity-chip',
          'data-entity-id': a.entityId,
          tabindex: '0',
          role: 'link',
        }),
      );
    }
    return true;
  });
  return DecorationSet.create(doc, decorations);
}

/** Escape a literal string for safe use inside a RegExp pattern. */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
